// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SchedulaTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Schedula"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of
        “Schedula” (the “App”). By downloading, installing, accessing, or using
        the App, you agree to be bound by these Terms. If you do not agree, you
        must uninstall the App and discontinue all use. Where required by
        platform rules or local law, additional terms may apply; in case of
        conflict with App Store or Google Play terms, the platform terms may
        prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        Schedula is a personal day planner and time-based schedule app. It lets
        you create tasks, attach them to time slots, view your day as a
        timeline, see a simple week view, and optionally receive local
        notifications as reminders. All core data (tasks, settings) is stored
        locally on your device; the App does not require an online account or
        external backend to function. Future versions may offer additional
        features such as themes or extended limits, which may be free or paid.
      </p>

      <div className={s.title}>3) Organizer-Only, Non-Advisory Purpose</div>
      <p>
        The App is an organizational and productivity tool only. It is not a
        provider of professional advice of any kind (including legal, medical,
        financial, or educational advice). Any labels, descriptions, or task
        texts you see in the App are either generic or entered by you and should
        not be treated as expert recommendations. You are solely responsible for
        deciding which tasks to create, how to prioritize your time, and how to
        act on reminders or schedules.
      </p>
      <p>
        The App is not designed for safety-critical use cases where missed
        reminders could cause harm (for example, emergency alerts, medical
        dosing schedules, or life-critical obligations). You should use
        appropriate specialised tools or professional guidance for such
        scenarios.
      </p>

      <div className={s.title}>4) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction, or with the
        consent and supervision of a parent or guardian where required. If you
        permit a minor to use the App, you are responsible for their use and for
        ensuring that it is appropriate in your circumstances.
      </p>

      <div className={s.title}>5) Local Storage and No Cloud Backup</div>
      <p>
        Schedula is designed to operate entirely on your device. Tasks, settings
        (such as theme and day start/end times), and reminder information are
        stored locally using technologies such as on-device databases or
        key-value storage. The App does not automatically sync your data to a
        remote server or between devices. If you uninstall the App, reset your
        device, or clear local data, your tasks and preferences may be lost and
        cannot be recovered by us.
      </p>
      <p>
        You are responsible for any manual backups or exports that may become
        available in future versions and for keeping your device secure,
        including the device-level lock screen and OS security settings.
      </p>

      <div className={s.title}>6) Tasks, Time Slots, and Reminders</div>
      <p>
        The App allows you to create tasks with titles, optional descriptions,
        dates, start and end times, and an optional reminder offset (for
        example, 5–60 minutes before the start time). Tasks are displayed in day
        and week views as time-based entries. You may mark tasks as completed,
        edit them, or delete them at any time.
      </p>
      <p>
        Reminder notifications are implemented as local notifications scheduled
        by your device based on the time information you provide. The accuracy
        and timely delivery of notifications depend on your device, operating
        system, battery and power saving settings, notification permissions, and
        other system-level factors outside our direct control. We cannot
        guarantee that every reminder will fire exactly at the requested time or
        at all.
      </p>

      <div className={s.title}>7) Notifications and Permissions</div>
      <p>
        To send local reminders, the App may request permission from your device
        to show notifications. You may grant or deny this permission and change
        it later in system settings. If you deny or revoke notification
        permissions, reminder functions may be limited or disabled, but you can
        still use the planner without alerts.
      </p>
      <p>
        Notification content is based on your own task entries (for example,
        using the task title as the notification text). Because notifications
        can appear on a locked screen, you should avoid entering sensitive or
        confidential information into task titles if you are concerned about
        visibility to others.
      </p>

      <div className={s.title}>8) Settings and Customization</div>
      <p>
        The App may offer simple settings such as theme selection (Light, Dark,
        or System), daily start and end hours, and basic information about the
        app. These settings are stored locally and affect how your schedule is
        displayed; they do not alter the underlying time data of your tasks.
        Changing the day start or end time may affect which blocks are visible
        on screen but does not delete any stored tasks.
      </p>

      <div className={s.title}>9) Free Use and Potential Premium Features</div>
      <p>
        The MVP version of Schedula may be available as a fully local planner
        without a paywall or Subscription. Future versions may introduce
        premium features (for example, design themes, larger task limits, or
        other enhancements) that could require in-app purchases or subscriptions
        managed by platform billing and services such as RevenueCat. If such
        features are added, details of pricing, trials, and benefits will be
        disclosed in the App and in the app stores, and separate subscription
        terms may apply in addition to these Terms.
      </p>

      <div className={s.title}>10) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, personal productivity purposes
        and not to:
      </p>
      <ul>
        <li>
          attempt to reverse engineer, decompile, or extract the source code of
          the App except where permitted by law;
        </li>
        <li>
          circumvent any technical protections, usage limits, or paywalls that
          may be introduced in later versions;
        </li>
        <li>
          use the App in a manner that could damage, disable, or overload
          devices or services;
        </li>
        <li>
          use the App to store or distribute illegal, infringing, or harmful
          content;
        </li>
        <li>
          misrepresent the App as providing professional advice or guarantees of
          outcomes.
        </li>
      </ul>

      <div className={s.title}>11) Intellectual Property</div>
      <p>
        You retain ownership of any original content you enter into the App
        (such as your task titles and descriptions), subject to the rights
        required for the App to process and display that content on your device.
        The App&apos;s software, design, layout, graphics, and branding
        (excluding your content and third-party assets) are owned by the
        publisher or its licensors and are protected by intellectual property
        laws.
      </p>
      <p>
        Subject to these Terms, you are granted a limited, non-exclusive,
        non-transferable, revocable license to use the App solely for personal
        scheduling and productivity purposes. You may not resell, redistribute,
        or sublicense the App or its components as a standalone product or as
        part of a competing service.
      </p>

      <div className={s.title}>12) Third-Party Technologies</div>
      <p>
        The App may use third-party libraries and platform services such as
        local databases (for example, SQLite or Hive), notification frameworks
        (such as flutter_local_notifications or platform equivalents),
        analytics, or crash reporting tools. These technologies help the App
        store data locally, schedule notifications, and diagnose technical
        issues. Where such tools process data, they may do so under their own
        terms and privacy policies, which you should review as applicable.
      </p>
      <p>
        The App does not rely on a dedicated external backend for task storage
        in the MVP. If cloud sync or remote services are introduced in the
        future, additional terms and privacy details will explain how they
        operate.
      </p>

      <div className={s.title}>13) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        Without limiting the foregoing, we do not warrant that:
      </p>
      <ul>
        <li>
          the App will always display your schedule correctly or without errors;
        </li>
        <li>
          notifications will always be delivered on time or at all;
        </li>
        <li>
          the App will be free of bugs, interruptions, or compatibility issues
          on all devices and OS versions;
        </li>
        <li>
          using the App will guarantee any particular productivity or time
          management outcome.
        </li>
      </ul>

      <div className={s.title}>14) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, including loss of time,
        missed appointments, lost data (such as deleted tasks or settings),
        productivity loss, or business interruption arising from or related to
        your use of, or inability to use, the App. Our total aggregate liability
        to you for direct damages arising out of or relating to the App will be
        limited to the amount you paid for the App or in-app purchases during
        the twelve (12) months prior to the claim, or the minimum amount
        permitted by applicable law if greater. Some jurisdictions do not allow
        certain limitations or exclusions, so parts of this section may not
        apply to you.
      </p>

      <div className={s.title}>15) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your breach of these Terms, or your
        violation of any rights of a third party.
      </p>

      <div className={s.title}>16) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Schedula (or, where applicable, a shared policy covering related apps),
        which explains how information is collected, used, stored, and shared.
        Because the MVP primarily stores data locally on your device, the
        Privacy Policy focuses on local storage, any analytics or crash
        diagnostics, and any future extensions such as cloud sync. In case of
        conflict between these Terms and the Privacy Policy regarding data
        processing, the Privacy Policy will govern how personal data is handled,
        while these Terms govern your use of the App and its features.
      </p>

      <div className={s.title}>17) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features, or modify these Terms
        from time to time. The “Last updated” date at the top of this document
        indicates when these Terms were last revised. Material changes may be
        highlighted in the App or communicated through other reasonable means.
        By continuing to use the App after changes take effect, you accept the
        updated Terms. If you do not agree to the revised Terms, you must stop
        using the App and uninstall it.
      </p>

      <div className={s.title}>18) Governing Law and Dispute Resolution</div>
      <p>
        Applicable law and dispute resolution mechanisms may depend on your
        country of residence and mandatory consumer protection rules. Where
        permitted, you agree to first contact us to attempt to resolve any
        dispute informally. Platform-specific dispute or refund procedures
        (Apple, Google) may also apply to billing matters if in-app purchases or
        subscriptions are introduced. Nothing in these Terms limits any
        non-waivable rights you may have under local law.
      </p>

      <div className={s.title}>19) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing any
        account-related or purchase-related matters in future versions that may
        include such features.
      </p>
    </TermOfUseLayout>
  );
}
